import type { JSONSchema } from 'json-schema-typed';
import { createMcpHandler } from 'mcp-handler';
import { APIError, type PayloadRequest } from 'payload';
import { buildMcpToolsForApp, executeTool } from './mcp.adapter';
import { LlmApp } from './types';

export const getMCPHandler = async (
  req: PayloadRequest,
  app: LlmApp,
  config?: Partial<Config>
) => {
  const { payload } = req;

  // Build MCP tool descriptors for this app
  const mcpTools = await buildMcpToolsForApp(app);

  const mcpServer = {
    capabilities: {
      tools: {},
    },
  };

  const mcpConfig: Config = {
    basePath: payload.config.routes?.api || '/api',
    maxDuration: 60,
    verboseLogs: false,
    ...config,
    disableSse: config?.disableSse ?? true,
  };

  try {
    return createMcpHandler((server) => {
      mcpTools.forEach(tool => {
        server.registerTool(
          tool.name,
          {
            title: tool.name,
            description: tool.description,
            inputSchema: tool.inputSchema as any,
          },
          async (params: any) => {
            const { name, arguments: args } = params as { name: string; arguments?: Record<string, unknown> };
            const result = await executeTool(req.payload, app, name, args as Record<string, unknown> || {});
            return {
              content: [{
                type: 'text',
                text: typeof result === 'string' ? result : JSON.stringify(result),
              } as any],
            };
          }
        );
      });
    }, mcpServer, mcpConfig);
  } catch (error) {
    throw new APIError(`Error initializing MCP handler: ${String(error)}`, 500);
  }
};

/**
 * Additional initialization options.
 */
export type ProtocolOptions = {
  /**
   * Whether to restrict emitted requests to only those that the remote side has indicated that they can handle, through their advertised capabilities.
   *
   * Note that this DOES NOT affect checking of _local_ side capabilities, as it is considered a logic error to mis-specify those.
   *
   * Currently this defaults to false, for backwards compatibility with SDK versions that did not advertise capabilities correctly. In future, this will default to true.
   */
  enforceStrictCapabilities?: boolean;
  /**
   * An array of notification method names that should be automatically debounced.
   * Any notifications with a method in this list will be coalesced if they
   * occur in the same tick of the event loop.
   * e.g., ['notifications/tools/list_changed']
   */
  debouncedNotificationMethods?: string[];
  /**
   * Optional task storage implementation. If provided, enables task-related request handlers
   * and provides task storage capabilities to request handlers.
   */
  taskStore?: unknown;
  /**
   * Optional task message queue implementation for managing server-initiated messages
   * that will be delivered through the tasks/result response stream.
   */
  taskMessageQueue?: unknown;
  /**
   * Default polling interval (in milliseconds) for task status checks when no pollInterval
   * is provided by the server. Defaults to 5000ms if not specified.
   */
  defaultTaskPollInterval?: number;
  /**
   * Maximum number of messages that can be queued per task for side-channel delivery.
   * If undefined, the queue size is unbounded.
   * When the limit is exceeded, the TaskMessageQueue implementation's enqueue() method
   * will throw an error. It's the implementation's responsibility to handle overflow
   * appropriately (e.g., by failing the task, dropping messages, etc.).
   */
  maxTaskQueueSize?: number;
};
/**
 * JSON Schema type definition (JSON Schema Draft 2020-12)
 *
 * This uses the object form of JSON Schema (excluding boolean schemas).
 * While `true` and `false` are valid JSON Schemas, this SDK uses the
 * object form for practical type safety.
 *
 * Re-exported from json-schema-typed for convenience.
 * @see https://json-schema.org/draft/2020-12/json-schema-core.html
 */
export type JsonSchemaType = JSONSchema.Interface;
/**
 * Result of a JSON Schema validation operation
 */
export type JsonSchemaValidatorResult<T> = {
  valid: true;
  data: T;
  errorMessage: undefined;
} | {
  valid: false;
  data: undefined;
  errorMessage: string;
};
/**
 * A validator function that validates data against a JSON Schema
 */
export type JsonSchemaValidator<T> = (input: unknown) => JsonSchemaValidatorResult<T>;
/**
 * Provider interface for creating validators from JSON Schemas
 *
 * This is the main extension point for custom validator implementations.
 * Implementations should:
 * - Support JSON Schema Draft 2020-12 (or be compatible with it)
 * - Return validator functions that can be called multiple times
 * - Handle schema compilation/caching internally
 * - Provide clear error messages on validation failure
 *
 * @example
 * ```typescript
 * class MyValidatorProvider implements jsonSchemaValidator {
 *   getValidator<T>(schema: JsonSchemaType<T>): JsonSchemaValidator<T> {
 *     // Compile/cache validator from schema
 *     return (input: unknown) => {
 *       // Validate input against schema
 *       if (valid) {
 *         return { valid: true, data: input as T, errorMessage: undefined };
 *       } else {
 *         return { valid: false, data: undefined, errorMessage: 'Error details' };
 *       }
 *     };
 *   }
 * }
 * ```
 */
export interface jsonSchemaValidator {
  /**
   * Create a validator for the given JSON Schema
   *
   * @param schema - Standard JSON Schema object
   * @returns A validator function that can be called multiple times
   */
  getValidator<T>(schema: JsonSchemaType): JsonSchemaValidator<T>;
}

export type ServerOptions = ProtocolOptions & {
  /**
   * Capabilities to advertise as being supported by this server.
   */
  capabilities?: unknown;
  /**
   * Optional instructions describing how to use the server and its features.
   */
  instructions?: string;
  /**
   * JSON Schema validator for elicitation response validation.
   *
   * The validator is used to validate user input returned from elicitation
   * requests against the requested schema.
   *
   * @default AjvJsonSchemaValidator
   *
   * @example
   * ```typescript
   * // ajv (default)
   * const server = new Server(
   *   { name: 'my-server', version: '1.0.0' },
   *   {
   *     capabilities: {}
   *     jsonSchemaValidator: new AjvJsonSchemaValidator()
   *   }
   * );
   *
   * // @cfworker/json-schema
   * const server = new Server(
   *   { name: 'my-server', version: '1.0.0' },
   *   {
   *     capabilities: {},
   *     jsonSchemaValidator: new CfWorkerJsonSchemaValidator()
   *   }
   * );
   * ```
   */
  jsonSchemaValidator?: jsonSchemaValidator;
};

type McpEventType = 'SESSION_STARTED' | 'SESSION_ENDED' | 'REQUEST_RECEIVED' | 'REQUEST_COMPLETED' | 'ERROR';

interface McpEventBase {
  type: McpEventType;
  timestamp: number;
  sessionId?: string;
  requestId?: string;
}

interface McpSessionEvent extends McpEventBase {
  type: 'SESSION_STARTED' | 'SESSION_ENDED';
  transport: 'SSE' | 'HTTP';
  clientInfo?: {
    userAgent?: string;
    ip?: string;
  };
}

interface McpRequestEvent extends McpEventBase {
  type: 'REQUEST_RECEIVED' | 'REQUEST_COMPLETED';
  method: string;
  parameters?: unknown;
  result?: unknown;
  duration?: number;
  status: 'success' | 'error';
}

interface McpErrorEvent extends McpEventBase {
  type: 'ERROR';
  error: Error | string;
  context?: string;
  source: 'request' | 'session' | 'system';
  severity: 'warning' | 'error' | 'fatal';
}

type McpEvent = McpSessionEvent | McpRequestEvent | McpErrorEvent;

/**
 * Configuration for the MCP handler.
 * @property redisUrl - The URL of the Redis instance to use for the MCP handler.
 * @property streamableHttpEndpoint - The endpoint to use for the streamable HTTP transport.
 * @property sseEndpoint - The endpoint to use for the SSE transport.
 * @property verboseLogs - If true, enables console logging.
 */
type Config = {
  /**
   * The URL of the Redis instance to use for the MCP handler.
   * @default process.env.REDIS_URL || process.env.KV_URL
   */
  redisUrl?: string;
  /**
   * The endpoint to use for the streamable HTTP transport.
   * @deprecated Use `set basePath` instead.
   * @default "/mcp"
   */
  streamableHttpEndpoint?: string;
  /**
   * The endpoint to use for the SSE transport.
   * @deprecated Use `set basePath` instead.
   * @default "/sse"
   */
  sseEndpoint?: string;
  /**
   * The endpoint to use for the SSE messages transport.
   * @deprecated Use `set basePath` instead.
   * @default "/message"
   */
  sseMessageEndpoint?: string;
  /**
   * The maximum duration of an MCP request in seconds.
   * @default 60
   */
  maxDuration?: number;
  /**
   * If true, enables console logging.
   * @default false
   */
  verboseLogs?: boolean;
  /**
   * The base path to use for deriving endpoints.
   * If provided, endpoints will be derived from this path.
   * For example, if basePath is "/", that means your routing is:
   *  /app/[transport]/route.ts and then:
   * - streamableHttpEndpoint will be "/mcp"
   * - sseEndpoint will be "/sse"
   * - sseMessageEndpoint will be "/message"
   * @default ""
   */
  basePath?: string;
  /**
   * Callback function that receives MCP events.
   * This can be used to track analytics, debug issues, or implement custom behaviors.
   */
  onEvent?: (event: McpEvent) => void;
  /**
   * If true, disables the SSE endpoint.
   * As of 2025-03-26, SSE is not supported by the MCP spec.
   * https://modelcontextprotocol.io/specification/2025-03-26/basic/transports
   * @default false
   */
  disableSse?: boolean;
  /**
   * sessionIdGenerator for the streamable HTTP transport
   */
  sessionIdGenerator?: undefined;
};
